import { Box, Tab, Tabs } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import classNames from 'classnames';
import { autobind } from 'core-decorators';
import moment from 'moment';
/* eslint-disable max-len */
import PropTypes from 'prop-types';
import { Component } from 'react';
import Alert from '/@/components/bayer/alert';
import Dots from '/@/components/dots';
import Toggle from '/@/components/forms/toggle';
import PlantingDatesTable from '/@/components/planting-dates-table';
import Title from '/@/components/title';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { formatDate, isMobile } from '/@/utils';
import { NewDotsReference } from '/@/v3/components/new-dots-reference';
import { RangeReference } from '/@/v3/components/range-reference';
import TitleV3 from '/@/v3/components/title';
import styles from './styles.module.css';

@autobind
export default class ProductList extends Component {
    static propTypes = {
        featuredProducts: PropTypes.array,
        otherProducts: PropTypes.array,
        selectedIds: PropTypes.array,
        plantingDate: PropTypes.string,
        metric: PropTypes.number,
        showEstimatedYield: PropTypes.bool,
        showShortDescription: PropTypes.bool,
        showPlantingDates: PropTypes.bool,
        onToggle: PropTypes.func.isRequired,
        showDefensiveProfile: PropTypes.bool,
        country: PropTypes.object,
        recommendation: PropTypes.object,
    };

    static defaultProps = {
        featuredProducts: [],
        otherProducts: [],
        selectedIds: [],
    };

    static contextTypes = {
        units: PropTypes.object,
    };

    state = {
        selectedTab: 0,
    };

    render() {
        const { featuredProducts, plantingDate } = this.props;

        if (this.props.showPlantingDates) {
            return (
                <PlantingDatesTable plantingDate={plantingDate} products={featuredProducts}>
                    {this.renderList}
                </PlantingDatesTable>
            );
        }

        return this.renderList();
    }

    renderList({ renderPlantingDatesHeader, renderPlantingDatesRow, renderPlantingDatesReferences } = {}) {
        const { featuredProducts, otherProducts } = this.props;

        if (featuredProducts.length === 0 && otherProducts.length === 0) {
            if (this.props.loading) return <Skeleton variant='rectangle' sx={{ width: '100%', height: 100, mt: 2 }} />;
            return <Alert severity='danger'>{_('No hay productos disponibles para los filtros seleccionados.')}</Alert>;
        }

        const referenceCodenames = [];
        featuredProducts.forEach((product) =>
            product.metrics.forEach(
                (metric) =>
                    metric.referenceCodename !== 'default' &&
                    !referenceCodenames.includes(metric.referenceCodename) &&
                    referenceCodenames.push(metric.referenceCodename)
            )
        );
        const useDefaultReference = referenceCodenames.length === 0;
        const newestProfileProductModified = moment.max([
            ...featuredProducts.map((p) => p.modified),
            ...otherProducts.map((p) => p.modified),
        ]);

        return (
            <Box>
                <Tabs
                    value={this.state.selectedTab}
                    onChange={(event: React.SyntheticEvent, newValue: number) => {
                        this.setState({
                            selectedTab: newValue,
                        });
                    }}
                    sx={{
                        pointerEvents: 'all',
                    }}
                >
                    {featuredProducts.length !== 0 && (
                        <Tab
                            label={
                                this.props.country.code === 'BR'
                                    ? _('Hìbridos preferenciales')
                                    : isMobile()
                                      ? _('Recomendados')
                                      : _('Recomendados para tu lote')
                            }
                        />
                    )}
                    {otherProducts.length !== 0 && (
                        <Tab
                            label={
                                this.props.country.code === 'BR'
                                    ? _('Hìbridos intermediários')
                                    : _('Otras opciones de nuestro portfolio')
                            }
                        />
                    )}
                </Tabs>

                {this.state.selectedTab === 0 &&
                    (featuredProducts.length !== 0
                        ? this.renderFeaturedProducts(renderPlantingDatesHeader, renderPlantingDatesRow)
                        : this.renderOtherProducts(renderPlantingDatesRow))}
                {this.state.selectedTab === 1 && this.renderOtherProducts(renderPlantingDatesRow)}

                {renderPlantingDatesReferences && renderPlantingDatesReferences()}
                {this.props.showDefensiveProfile && this.props.metric && (
                    <div style={{ marginTop: 24, maxWidth: '50%' }}>
                        <div className={styles.title}>
                            <Title size='md'>{_('Referencias de perfil defensivo')}</Title>
                        </div>
                        <div>
                            {useDefaultReference ? (
                                <NewDotsReference
                                    brand={featuredProducts?.[0]?.product.brand}
                                    country={this.props.country}
                                />
                            ) : (
                                <RangeReference
                                    hideTitle
                                    maxWidth={false}
                                    referenceCodenames={referenceCodenames}
                                    theme={featuredProducts?.[0]?.product.brand.theme}
                                />
                            )}
                        </div>
                    </div>
                )}
                <Stack alignItems={'end'} my={2}>
                    <TitleV3 size='xs'>
                        {_('Última fecha de actualización de datos {{ date }}.', {
                            date: formatDate(new Date(newestProfileProductModified)),
                        })}
                    </TitleV3>
                </Stack>
            </Box>
        );
    }

    renderFeaturedProducts(renderPlantingDatesHeader, renderPlantingDatesRow) {
        const { featuredProducts, showEstimatedYield } = this.props;

        return (
            <div className={styles.featuredProducts}>
                <div className={styles.header}>
                    {showEstimatedYield && (
                        <div className={styles.estimatedYieldReference}>
                            {_('Rendimiento estimado')} (
                            {this.context.units.getUnit('yield', featuredProducts?.[0]?.product.brand.type)})
                        </div>
                    )}
                    {renderPlantingDatesHeader && renderPlantingDatesHeader()}
                </div>
                <div
                    className={classNames(
                        styles.ranking,
                        settings.app.theme === 'expertobayer'
                            ? styles[`theme-${settings.app.theme}`]
                            : styles[`theme-${featuredProducts?.[0]?.product.brand.theme}`]
                    )}
                >
                    {this.getRanking(featuredProducts).map((ranking) => (
                        <div key={ranking.index} className={styles.rankingRow}>
                            {featuredProducts?.[0]?.product.brand.type !== 'soy' && (
                                <div className={styles.rankingValue}>#{ranking.index}</div>
                            )}
                            {this.renderProducts(ranking.products, renderPlantingDatesRow, showEstimatedYield)}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    renderOtherProducts(renderPlantingDatesRow) {
        const { otherProducts } = this.props;
        if (!otherProducts.length) return null;

        return (
            <div className={this.props.featuredProducts.length > 0 && styles.otherProducts}>
                <div className={styles.rankingRow}>
                    {this.renderProducts(otherProducts, renderPlantingDatesRow, false, false)}
                </div>
            </div>
        );
    }

    renderProducts(products, renderPlantingDatesRow, showEstimatedYield, allowSpaceBetween = true) {
        const { selectedIds, metric, showShortDescription } = this.props;

        return (
            <div className={styles.products}>
                {products.map((product, index) => {
                    const selected = selectedIds.includes(product.id);
                    const disabled = selectedIds.length === settings.maxProductSelection;

                    const mainClassName = classNames(styles.main, {
                        [styles.spaceBetween]: allowSpaceBetween && !showEstimatedYield && !renderPlantingDatesRow,
                    });
                    const rowClassNames = classNames(
                        styles.productRow,
                        settings.app.theme === 'expertobayer'
                            ? styles[`theme-${settings.app.theme}`]
                            : styles[`theme-${product.product.brand.theme}`],
                        ((disabled && !selected) || this.props.loading) && styles.disabled
                    );

                    return (
                        <div
                            key={product.id}
                            data-testid={`recommendation-selection-product-row-${index}`}
                            className={rowClassNames}
                            onClick={this.props.onToggle(product)}
                        >
                            <div className={mainClassName}>
                                <div className={styles.product}>
                                    <div className={styles.productName}>
                                        {this.props.loading ? (
                                            <Skeleton
                                                variant='text'
                                                sx={{ fontSize: '2rem', width: 100, height: '100%' }}
                                            />
                                        ) : (
                                            this.renderProduct({ product, selected, disabled })
                                        )}

                                        {product.nutritionalInfo &&
                                            product.nutritionalInfo.length > 0 &&
                                            (this.props.loading ? (
                                                <Skeleton
                                                    variant='text'
                                                    sx={{ fontSize: '2rem', width: 100, height: '100%', mx: 2 }}
                                                />
                                            ) : (
                                                <div className={styles.hasNutritionalInfo}>
                                                    {_('Apto para forraje')}
                                                </div>
                                            ))}
                                    </div>
                                    {showShortDescription && this.renderShortDescription({ product })}
                                </div>
                                {metric && this.renderMetric({ product, metric })}
                            </div>
                            {showEstimatedYield && this.renderEstimatedYield({ product })}
                            {renderPlantingDatesRow && renderPlantingDatesRow(product)}
                        </div>
                    );
                })}
            </div>
        );
    }

    renderProduct({ product, selected, disabled }) {
        return (
            <div className={styles.checkbox}>
                <Toggle
                    type='checkbox'
                    theme={
                        !disabled || selected
                            ? settings.app.theme === 'expertobayer'
                                ? 'expertobayer'
                                : product.product.brand.theme
                            : undefined
                    }
                    label={product.product.catalogName}
                    value={selected}
                />
            </div>
        );
    }

    renderShortDescription({ product }) {
        const value = _(product.shortDescription);
        if (!value) return null;
        if (this.props.loading) return <Skeleton variant='text' sx={{ fontSize: '2rem', height: '100%' }} />;
        return <div className={styles.shortDescription}>{value}</div>;
    }

    renderMetric({ product, metric }) {
        const metricValue = product.metrics.find(({ id }) => id === metric);

        if (!metricValue) return null;
        const { brand } = product.product;
        return (
            <div className={styles.metric}>
                <Dots max={brand.metricsMaxValue} value={metricValue.value} showValues={brand.showMetricsValues} />
            </div>
        );
    }

    renderEstimatedYield({ product }) {
        const estimatedYield = product.recommended ? product.recommended.yield : '-';
        const error = this.context.units.toYield(estimatedYield, product.product.brand.type).localeValue === 'NaN';

        return (
            <div className={styles.estimatedYield}>
                {this.props.loading ? (
                    <Skeleton variant='text' sx={{ fontSize: '2rem', width: 60 }} />
                ) : (
                    <Tooltip
                        title={error ? _('No hemos podido calcular el rendimiento estimado para este híbrido') : false}
                        disableHoverListener={!error}
                    >
                        <span>
                            {error
                                ? '-'
                                : this.context.units.toYield(estimatedYield, product.product.brand.type).localeValue}
                        </span>
                    </Tooltip>
                )}
            </div>
        );
    }

    getRanking(products) {
        return Object.values(
            products.reduce((result, product) => {
                const { ranking } = product;
                if (!result[ranking]) result[ranking] = { index: ranking, products: [] };
                result?.[ranking]?.products.push(product);
                return result;
            }, {})
        );
    }
}
